import {
  Button,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@predoc/cow';
import {
  ArrowLeft,
  Users,
  Building2,
  Download,
  Info,
  ChevronDown,
} from 'lucide-react';
import { PDFViewer } from './PDFViewer';
import { PageHeader } from './PageHeader';
import {
  getFileUrl,
  sanitizeFilename,
  downloadFromUrl,
} from '@/utils/downloadUtils';
import { Permission, useHasPermission } from '@/utils/roles';

export interface DocumentViewerModalProps {
  open: boolean;
  onClose: () => void;
  file: string | File | { url: string };
  title?: string;
  uploadDate?: string;
  userName?: string;
  profileName?: string;
  patientKey?: string;
  isSplicedFile?: boolean;
}

export const DocumentViewerModal = ({
  open,
  onClose,
  file,
  title = 'Document',
  uploadDate,
  userName,
  profileName,
  patientKey = '',
  isSplicedFile = false,
}: DocumentViewerModalProps) => {
  const { hasPermission: canExport } = useHasPermission(
    Permission.EXPORT_MEDICAL_RECORDS,
  );

  const handleDownload = () => {
    const url = getFileUrl(file);
    const filename = sanitizeFilename(`${title}-${patientKey}`);
    downloadFromUrl(url, `${filename}.pdf`);
  };

  return (
    <Sheet open={open} onOpenChange={(isOpen: boolean) => !isOpen && onClose()}>
      <SheetContent
        fullScreen
        showCloseButton={false}
        className="flex flex-col gap-0 p-0"
      >
        <SheetHeader className="sr-only">
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>
        <PageHeader>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              aria-label="Close document viewer"
              className="size-9"
            >
              <ArrowLeft className="size-5" />
            </Button>
            <h1 className="text-lg font-semibold text-gray-900">{title}</h1>
          </div>

          <div className="flex items-center gap-6">
            {uploadDate && (
              <span className="flex items-center gap-1 text-sm text-gray-600">
                Uploaded {uploadDate}
              </span>
            )}
            {userName && (
              <span className="flex items-center gap-1 text-sm text-gray-600">
                <Users className="size-4" />
                {userName}
              </span>
            )}
            {profileName && (
              <span className="flex items-center gap-1 text-sm text-gray-600">
                <Building2 className="size-4" />
                {profileName}
              </span>
            )}
            {canExport ? (
              <Button
                variant="outline"
                className="flex items-center gap-2"
                onClick={handleDownload}
                aria-label="Download PDF"
              >
                <Download className="size-4" />
                Download
                {/* TODO: for spliced and full doc, perform different actions */}
                <ChevronDown className="size-4" />
              </Button>
            ) : null}
          </div>
        </PageHeader>

        {isSplicedFile && (
          <div className="flex items-center gap-2 bg-neutral-800 px-4 py-2 text-sm text-white">
            <Info className="size-4 shrink-0" />
            <span>
              You are viewing a spliced document that focuses on a specific
              medical record segment. Access the full source document by
              downloading it from the menu above
            </span>
          </div>
        )}

        <div className="flex-1 overflow-hidden">
          <PDFViewer file={file} className="h-full" />
        </div>
      </SheetContent>
    </Sheet>
  );
};
